/**
 * Serviço de integração com Google Places API
 */

import { BusinessInfo, BusinessSector, Coordinates } from '../types';

export class GooglePlacesService {
  private placesService: google.maps.places.PlacesService | null = null;

  /**
   * Inicializa o serviço com o mapa
   */
  initialize(map: google.maps.Map) {
    this.placesService = new google.maps.places.PlacesService(map);
  }

  /**
   * Busca estabelecimentos próximos COM MÚLTIPLAS BUSCAS
   * Estratégia: Fazer 3 buscas em círculos concêntricos para obter mais resultados
   */
  async searchNearby(
    center: Coordinates,
    radius: number,
    sector: BusinessSector
  ): Promise<BusinessInfo[]> {
    if (!this.placesService) {
      throw new Error('PlacesService não inicializado');
    }

    console.log(`🔍 Buscando ${sector} em um raio de ${radius}m...`);
    console.log(`📄 Usando estratégia de múltiplas buscas para obter até 60 resultados...`);
    
    const allResults: BusinessInfo[] = [];
    
    // Estratégia: Fazer buscas em 3 círculos concêntricos
    // Círculo 1: raio completo (centro principal)
    // Círculo 2: raio reduzido 33% deslocado para norte
    // Círculo 3: raio reduzido 33% deslocado para sul
    
    const searches = [
      { center, radius, label: 'centro' },
      { center: this.offsetCoordinates(center, radius * 0.5, 0), radius: radius * 0.7, label: 'norte' },
      { center: this.offsetCoordinates(center, radius * 0.5, 180), radius: radius * 0.7, label: 'sul' }
    ];
    
    for (let i = 0; i < searches.length; i++) {
      const search = searches[i];
      console.log(`🔍 Busca ${i + 1}/3 (${search.label})...`);
      
      try {
        const results = await this.searchByType(search.center, search.radius, sector);
        allResults.push(...results);
        console.log(`✅ Busca ${i + 1}: ${results.length} estabelecimentos encontrados (Total: ${allResults.length})`);
        
        // Aguardar entre buscas para não exceder rate limit
        if (i < searches.length - 1) {
          await this.sleep(500);
        }
      } catch (error) {
        console.error(`❌ Erro na busca ${i + 1}:`, error);
      }
    }
    
    console.log(`🔍 Total bruto: ${allResults.length} estabelecimentos`);
    
    // Remove duplicatas baseado no placeId
    const uniqueResults = this.removeDuplicates(allResults);
    console.log(`✅ Após remover duplicatas: ${uniqueResults.length} estabelecimentos únicos`);
    
    // BUSCAR DETALHES COMPLETOS DE CADA ESTABELECIMENTO
    console.log(`🔍 Buscando detalhes completos...`);
    const detailedResults = await this.enrichWithDetails(uniqueResults);
    
    return detailedResults;
  }

  /**
   * Desloca coordenadas em uma direção e distância específicas
   * @param center Coordenadas de origem
   * @param distanceMeters Distância em metros
   * @param bearingDegrees Direção em graus (0=norte, 90=leste, 180=sul, 270=oeste)
   */
  private offsetCoordinates(center: Coordinates, distanceMeters: number, bearingDegrees: number): Coordinates {
    const earthRadiusMeters = 6371000;
    const bearingRadians = (bearingDegrees * Math.PI) / 180;
    const latRadians = (center.lat * Math.PI) / 180;
    const lngRadians = (center.lng * Math.PI) / 180;
    const angularDistance = distanceMeters / earthRadiusMeters;

    const newLatRadians = Math.asin(
      Math.sin(latRadians) * Math.cos(angularDistance) +
      Math.cos(latRadians) * Math.sin(angularDistance) * Math.cos(bearingRadians)
    );

    const newLngRadians = lngRadians + Math.atan2(
      Math.sin(bearingRadians) * Math.sin(angularDistance) * Math.cos(latRadians),
      Math.cos(angularDistance) - Math.sin(latRadians) * Math.sin(newLatRadians)
    );

    return {
      lat: (newLatRadians * 180) / Math.PI,
      lng: (newLngRadians * 180) / Math.PI
    };
  }

  /**
   * Enriquece resultados com detalhes completos (telefone, website, etc)
   */
  private async enrichWithDetails(businesses: BusinessInfo[]): Promise<BusinessInfo[]> {
    const enrichedBusinesses: BusinessInfo[] = [];
    
    for (const business of businesses) {
      if (business.placeId) {
        try {
          const details = await this.getPlaceDetails(business.placeId);
          const enriched = {
            ...business,
            ...details,
            dataQuality: this.calculateEnrichedQuality({ ...business, ...details })
          };
          
          // FILTRO: Apenas empresas com telefone
          if (enriched.phone) {
            enrichedBusinesses.push(enriched);
            console.log(`✅ Detalhes obtidos: ${business.name} (Tel: ${enriched.phone})`);
          } else {
            console.log(`⏭️  Ignorado (sem telefone): ${business.name}`);
          }
        } catch (error) {
          console.error(`❌ Erro ao buscar detalhes de ${business.name}:`, error);
          // Não adiciona se der erro
        }
      }
    }
    
    console.log(`📊 Total com telefone: ${enrichedBusinesses.length} de ${businesses.length} estabelecimentos`);
    return enrichedBusinesses;
  }

  /**
   * Busca por tipo do Google Places
   */
  private searchByType(
    center: Coordinates,
    radius: number,
    sector: BusinessSector
  ): Promise<BusinessInfo[]> {
    return new Promise((resolve, reject) => {
      if (!this.placesService) {
        reject(new Error('PlacesService não inicializado'));
        return;
      }

      const request: google.maps.places.PlaceSearchRequest = {
        location: new google.maps.LatLng(center.lat, center.lng),
        radius: radius,
        type: sector
      };

      this.placesService.nearbySearch(request, (results, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && results) {
          const businesses = results.map(place => this.convertPlaceToBusinessInfo(place));
          resolve(businesses);
        } else if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          resolve([]);
        } else {
          console.error(`❌ Erro na busca: ${status}`);
          reject(new Error(`Erro na busca: ${status}`));
        }
      });
    });
  }

  /**
   * Função auxiliar para aguardar (sleep)
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Busca detalhes COMPLETOS de um estabelecimento
   */
  async getPlaceDetails(placeId: string): Promise<Partial<BusinessInfo>> {
    return new Promise((resolve, reject) => {
      if (!this.placesService) {
        reject(new Error('PlacesService não inicializado'));
        return;
      }

      const request: google.maps.places.PlaceDetailsRequest = {
        placeId: placeId,
        fields: [
          'name',
          'formatted_address',
          'address_components',
          'geometry',
          'formatted_phone_number',
          'international_phone_number',
          'website',
          'rating',
          'user_ratings_total',
          'opening_hours',
          'photos',
          'url',
          'types',
          'business_status'
        ]
      };

      this.placesService.getDetails(request, (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
          // Extrair telefone (tentar formatos diferentes)
          const phone = place.formatted_phone_number || place.international_phone_number;
          
          // WhatsApp é geralmente o mesmo que telefone no Brasil
          const whatsapp = phone ? this.formatPhoneForWhatsApp(phone) : undefined;
          
          // Extrair cidade e bairro dos address_components
          let city = '';
          let neighborhood = '';
          let state = '';
          
          if (place.address_components) {
            for (const component of place.address_components) {
              if (component.types.includes('locality')) {
                city = component.long_name;
              }
              if (component.types.includes('sublocality') || component.types.includes('sublocality_level_1')) {
                neighborhood = component.long_name;
              }
              if (component.types.includes('administrative_area_level_1')) {
                state = component.short_name;
              }
            }
          }
          
          const details: Partial<BusinessInfo> = {
            phone: phone,
            whatsapp: whatsapp,
            website: place.website,
            rating: place.rating,
            userRatingsTotal: place.user_ratings_total,
            openingHours: place.opening_hours?.weekday_text,
            photos: place.photos?.map(photo => photo.getUrl({ maxWidth: 400 })),
            city: city,
            neighborhood: neighborhood,
            state: state
          };
          
          console.log(`📞 ${place.name}: ${neighborhood}, ${city} - Tel=${phone}`);
          resolve(details);
        } else {
          reject(new Error(`Erro ao buscar detalhes: ${status}`));
        }
      });
    });
  }

  /**
   * Formata telefone para WhatsApp (remove caracteres especiais)
   */
  private formatPhoneForWhatsApp(phone: string): string {
    // Remove tudo exceto números
    let cleaned = phone.replace(/\D/g, '');
    
    // Se começar com 0, remove
    if (cleaned.startsWith('0')) {
      cleaned = cleaned.substring(1);
    }
    
    // Se não tem código do país e é brasileiro (começa com DDD), adiciona 55
    if (cleaned.length === 10 || cleaned.length === 11) {
      cleaned = '55' + cleaned;
    }
    
    return cleaned;
  }

  /**
   * Converte resultado do Google Places para BusinessInfo
   */
  private convertPlaceToBusinessInfo(place: google.maps.places.PlaceResult): BusinessInfo {
    const coordinates: Coordinates = {
      lat: place.geometry?.location?.lat() || 0,
      lng: place.geometry?.location?.lng() || 0
    };

    return {
      id: place.place_id || `temp_${Date.now()}_${Math.random()}`,
      name: place.name || 'Nome não disponível',
      address: place.vicinity || place.formatted_address || 'Endereço não disponível',
      coordinates,
      placeId: place.place_id,
      phone: place.formatted_phone_number,
      rating: place.rating,
      userRatingsTotal: place.user_ratings_total,
      source: 'google_places',
      dataQuality: this.calculateDataQuality(place),
      lastUpdated: new Date()
    };
  }

  /**
   * Calcula qualidade dos dados (0-100)
   */
  private calculateDataQuality(place: google.maps.places.PlaceResult): number {
    let quality = 0;
    
    if (place.name) quality += 20;
    if (place.formatted_address || place.vicinity) quality += 20;
    if (place.geometry?.location) quality += 20;
    if (place.formatted_phone_number) quality += 15;
    if (place.website) quality += 15;
    if (place.rating && place.user_ratings_total) quality += 10;

    return Math.min(quality, 100);
  }

  /**
   * Calcula qualidade dos dados enriquecidos (0-100)
   */
  private calculateEnrichedQuality(business: any): number {
    let quality = 0;
    
    if (business.name) quality += 15;
    if (business.address) quality += 15;
    if (business.coordinates) quality += 10;
    if (business.phone) quality += 20;
    if (business.whatsapp) quality += 15;
    if (business.email) quality += 10;
    if (business.website) quality += 15;

    return Math.min(quality, 100);
  }


  /**
   * Remove duplicatas baseado no placeId
   */
  private removeDuplicates(businesses: BusinessInfo[]): BusinessInfo[] {
    const seen = new Set<string>();
    return businesses.filter(business => {
      if (!business.placeId) return true;
      if (seen.has(business.placeId)) return false;
      seen.add(business.placeId);
      return true;
    });
  }
}

// Instância singleton
export const googlePlacesService = new GooglePlacesService();

